/**
 * SQ-441 / SQ-523: what a machine on a work item costs, from the values
 * locked on its assignment row, and the per-machine minimum top-up a job
 * adds once. Mirror of Android's `TreeDtos.kt` `lockedCost()` and
 * `WorkItemsState.equipmentCharges()`, and of the server's
 * `estimate_equipment_cost` view (V60); all three run the vectors in
 * `docs/fixtures/equipment-charges-vectors.json`.
 *
 * A row at 0 minutes is free (that is how "picked but not assigned" is
 * stored). Rows from before V44 keep their cents; later rows bill to the
 * nearest dollar. Rows from before V60 have no dollar minimum and keep
 * their own minimum billable minutes instead.
 */

export interface EquipmentChargeRow {
  equipmentId: number;
  durationMinutes: number;
  hourlyRate: number | null;
  minimumBillableMinutes: number;
  minimumCharge: number | null;
  wholeDollars: boolean;
}

/** The row's own price: rate × max(minutes, minimum minutes) / 60; 0 minutes is free. */
export function lockedCost(row: EquipmentChargeRow): number {
  if (row.hourlyRate === null || row.durationMinutes <= 0) return 0;
  const minutes = Math.max(row.durationMinutes, row.minimumBillableMinutes);
  const cost = row.hourlyRate * minutes / 60;
  return row.wholeDollars ? Math.round(cost) : Math.round(cost * 100) / 100;
}

export interface EquipmentMinimumTopUp {
  equipmentId: number;
  /** The machine's locked dollar minimum (the largest across its rows). */
  minimumCharge: number;
  /** What its rows bill across the record. */
  billed: number;
  /** The difference the job total adds once, when billed falls short. */
  topUp: number;
}

/**
 * Per machine across the record: the largest locked minimum, less the rows'
 * summed cost, when positive. A machine whose every row bills no time is not
 * on the record for this (the view takes `duration_minutes > 0` rows only, and
 * so does Android): picked but unassigned costs nothing, minimum included.
 */
export function equipmentCharges(rows: EquipmentChargeRow[]): EquipmentMinimumTopUp[] {
  const byMachine = new Map<number, {minimum: number; billed: number}>();
  rows.filter(row => row.durationMinutes > 0).forEach(row => {
    const entry = byMachine.get(row.equipmentId) ?? {minimum: 0, billed: 0};
    entry.minimum = Math.max(entry.minimum, row.minimumCharge ?? 0);
    entry.billed += lockedCost(row);
    byMachine.set(row.equipmentId, entry);
  });
  return Array.from(byMachine.entries())
    .filter(([, entry]) => entry.minimum > entry.billed)
    .map(([equipmentId, entry]) => ({
      equipmentId, minimumCharge: entry.minimum, billed: entry.billed,
      topUp: Math.round((entry.minimum - entry.billed) * 100) / 100,
    }));
}

/** Everything the record's equipment comes to: the rows plus the top-ups. */
export function equipmentTotal(rows: EquipmentChargeRow[]): number {
  const billed = rows.reduce((sum, row) => sum + lockedCost(row), 0);
  const topUps = equipmentCharges(rows).reduce((sum, charge) => sum + charge.topUp, 0);
  return Math.round((billed + topUps) * 100) / 100;
}
